import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiPlayCircle, FiCheckCircle, FiClock, FiMapPin, FiUsers, FiArrowRight, FiAlertCircle } from 'react-icons/fi';
import api from '../../utils/api';
import '../../styles/mobileResponsive.css';

const font = "'Outfit', sans-serif";
const C = { text: '#0f172a', sub: '#64748b', border: '#bae6fd', bg: '#f0f9ff', white: '#fff', accent: '#0ea5e9' };

const ExamControllerDashboard = () => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/api/exam-controller/sessions')
            .then(r => setSessions(r.data.sessions))
            .catch(e => console.error(e))
            .finally(() => setLoading(false));
    }, []);

    const scheduled = sessions.filter(s => s.status === 'scheduled').length;
    const live = sessions.filter(s => s.status === 'active').length;
    const ended = sessions.filter(s => s.status === 'ended').length;
    const liveSessions = sessions.filter(s => s.status === 'active');

    const getTime = () => {
        const now = new Date();
        return now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    return (
        <div style={{ fontFamily: font }}>
            {/* Welcome Banner — responsive */}
            <div style={{ background: 'linear-gradient(135deg,#0ea5e9,#38bdf8)', borderRadius: 20, padding: '24px 20px', marginBottom: 24, color: '#fff', boxShadow: '0 8px 24px rgba(14,165,233,0.25)' }}>
                <div className="resp-banner">
                    <div>
                        <h1 style={{ fontSize: 22, fontWeight: 900, margin: '0 0 6px' }}>Exam Controller</h1>
                        <p style={{ margin: 0, opacity: 0.85, fontSize: 14 }}>
                            {live > 0
                                ? `⚡ ${live} session${live > 1 ? 's' : ''} currently LIVE`
                                : 'Manage exam sessions, halls, and seat allocations.'}
                        </p>
                    </div>
                    <div style={{ opacity: 0.85 }}>
                        <div style={{ fontSize: 26, fontWeight: 900 }}>{getTime()}</div>
                        <div style={{ fontSize: 12 }}>{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
                    </div>
                </div>
            </div>

            {/* Stat Cards — 4col → 2col */}
            <div className="resp-grid-4" style={{ marginBottom: 24 }}>
                {[
                    { label: 'Total Sessions', val: sessions.length, icon: <FiCalendar />, c: '#0ea5e9', bg: '#e0f2fe', link: '/exam-controller/sessions' },
                    { label: 'Scheduled', val: scheduled, icon: <FiClock />, c: '#d97706', bg: '#fef3c7', link: '/exam-controller/sessions' },
                    { label: 'Live Now', val: live, icon: <FiPlayCircle />, c: '#059669', bg: '#d1fae5', link: '/exam-controller/sessions' },
                    { label: 'Ended', val: ended, icon: <FiCheckCircle />, c: '#64748b', bg: '#f1f5f9', link: '/exam-controller/sessions' },
                ].map((s, i) => (
                    <div key={i} onClick={() => navigate(s.link)} style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`, padding: '16px 14px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(14,165,233,0.12)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: s.bg, color: s.c, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{s.icon}</div>
                        <div>
                            <div style={{ fontSize: 26, fontWeight: 900, color: C.text, lineHeight: 1 }}>{loading ? '—' : s.val}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 3 }}>{s.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Two-col → One-col on mobile */}
            <div className="resp-2col">
                {/* Live Sessions & Upcoming */}
                <div>
                    <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, overflow: 'hidden', marginBottom: 20 }}>
                        <div style={{ padding: '14px 20px', background: live > 0 ? '#d1fae5' : C.bg, borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 800, fontSize: 14, color: C.text }}>{live > 0 ? '🔴 Live Sessions' : 'Live Sessions'}</span>
                            <span style={{ fontSize: 12, color: live > 0 ? '#059669' : C.sub, fontWeight: 700 }}>{live} active</span>
                        </div>
                        {liveSessions.length === 0 ? (
                            <div style={{ padding: '24px 20px', textAlign: 'center', color: C.sub, fontSize: 13 }}>
                                <FiAlertCircle size={28} style={{ opacity: 0.3, marginBottom: 8, display: 'block', margin: '0 auto 10px' }} />
                                No live sessions right now.
                            </div>
                        ) : (
                            liveSessions.map(s => (
                                <div key={s.id} style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{s.subject_name}</div>
                                        <div style={{ fontSize: 11, color: C.sub }}>{s.department} • Sem {s.semester} • {s.hall_count || 0} halls active</div>
                                    </div>
                                    <span style={{ background: '#d1fae5', color: '#059669', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 800, flexShrink: 0 }}>LIVE 🔴</span>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Upcoming Scheduled */}
                    <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                        <div style={{ padding: '14px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, fontWeight: 800, fontSize: 14, color: C.text }}>Upcoming Scheduled</div>
                        {sessions.filter(s => s.status === 'scheduled').slice(0, 4).length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: C.sub, fontSize: 13 }}>No scheduled sessions.</div>
                        ) : (
                            sessions.filter(s => s.status === 'scheduled').slice(0, 4).map(s => (
                                <div key={s.id} style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 800, color: C.text }}>{s.subject_name}</div>
                                        <div style={{ fontSize: 11, color: C.sub }}>{s.exam_date?.slice(0, 10)} • {s.department}</div>
                                    </div>
                                    <span style={{ fontSize: 10, fontWeight: 800, color: '#d97706', background: '#fef3c7', padding: '2px 8px', borderRadius: 20, flexShrink: 0 }}>Scheduled</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div>
                    <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                        <div style={{ padding: '14px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, fontWeight: 800, fontSize: 14, color: C.text }}>Quick Actions</div>
                        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {[
                                { icon: <FiCalendar size={20} />, title: 'Manage Exam Sessions', sub: 'Create, start and end exam sessions', link: '/exam-controller/sessions', c: '#0ea5e9', bg: '#e0f2fe' },
                                { icon: <FiMapPin size={20} />, title: 'Assign Halls & Invigilators', sub: 'Link halls and invigilators to sessions', link: '/exam-controller/assign-halls', c: '#8b5cf6', bg: '#ede9fe' },
                                { icon: <FiUsers size={20} />, title: 'Seat Plan Management', sub: 'Allocate students to specific seats', link: '/exam-controller/seat-plan', c: '#059669', bg: '#d1fae5' },
                                { icon: <FiUsers size={20} />, title: 'Invigilator Overview', sub: 'See who is assigned to which hall & exam', link: '/exam-controller/invigilators', c: '#e11d48', bg: '#fce7f3' },
                            ].map((action, i) => (
                                <div key={i} onClick={() => navigate(action.link)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 14, border: `1.5px solid ${C.border}`, cursor: 'pointer', background: '#fff' }}
                                    onMouseEnter={e => e.currentTarget.style.background = C.bg}
                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                    <div style={{ width: 44, height: 44, borderRadius: 12, background: action.bg, color: action.c, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{action.icon}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{action.title}</div>
                                        <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>{action.sub}</div>
                                    </div>
                                    <FiArrowRight size={16} color={C.sub} style={{ flexShrink: 0 }} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Exam Flow Guide */}
                    <div style={{ marginTop: 20, background: 'linear-gradient(135deg,#f0f9ff,#e0f2fe)', borderRadius: 20, border: `1.5px solid ${C.border}`, padding: '18px 20px' }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 14 }}>📋 Exam Day Flow</div>
                        {[
                            { step: 1, text: 'Create a session from an exam schedule' },
                            { step: 2, text: 'Assign halls & invigilators to the session' },
                            { step: 3, text: 'Allocate students to seats per hall' },
                            { step: 4, text: 'Start the session — invigilators can begin' },
                            { step: 5, text: 'Invigilators start their hall → students see paper' },
                            { step: 6, text: 'Invigilators upload answer sheets per seat' },
                            { step: 7, text: 'End the session when all halls finish' },
                        ].map(({ step, text }) => (
                            <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                <div style={{ width: 22, height: 22, borderRadius: '50%', background: C.accent, color: '#fff', fontSize: 11, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{step}</div>
                                <div style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>{text}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExamControllerDashboard;
